import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, HeartHandshake, Home, Building2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";

const SIGNIN_URL = process.env.REACT_APP_SIGNIN_URL || "https://app.ayobell.com/login";

const solutions = [
  { label: "Supported Living", to: "/supported-living", icon: HeartHandshake, desc: "Independence with better team visibility" },
  { label: "Residential Care", to: "/residential-care", icon: Building2, desc: "Connected care across your home" },
  { label: "Domiciliary Care", to: "/domiciliary-care", icon: Home, desc: "From scheduling to care delivery" },
];

const navLinks = [
  { label: "Platform", to: "/platform" },
  { label: "CQC & Governance", to: "/cqc-governance" },
  { label: "Data Migration", to: "/data-migration" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
];

function handleSignIn(e) {
  if (SIGNIN_URL) {
    window.location.href = SIGNIN_URL;
  } else {
    e.preventDefault();
    toast.info("Sign-in will be available once your AYOBELL login URL is confirmed.");
  }
}

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 pr-6 group">
      <img
        src="/ayobell-logo.png"
        alt="AYOBELL Care Management Platform"
        className="w-11 h-11 md:w-12 md:h-12 rounded-xl object-contain shadow-lg shadow-teal-900/30 ring-1 ring-white/10 group-hover:scale-105 transition-transform"
      />
      <span className="font-display font-extrabold text-xl tracking-tight text-white leading-none">AYOBELL</span>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  return (
    <>
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen ? "bg-slate-900/90 backdrop-blur-xl border-b border-white/10 shadow-lg" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-1">
          <div
            className="relative"
            onMouseEnter={() => setSolOpen(true)}
            onMouseLeave={() => setSolOpen(false)}
          >
            <Link
              to="/solutions"
              data-testid="nav-solutions-trigger"
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-200 hover:text-white transition-colors rounded-lg"
            >
              Solutions <ChevronDown className={`w-4 h-4 transition-transform ${solOpen ? "rotate-180" : ""}`} />
            </Link>
            <AnimatePresence>
              {solOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute top-full left-0 pt-2 w-80"
                >
                  <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-2">
                    {solutions.map((s) => (
                      <Link
                        key={s.label}
                        to={s.to}
                        data-testid={`nav-solution-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-teal-50 transition-colors group"
                      >
                        <span className="mt-0.5 flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 text-teal-600 group-hover:bg-teal-100">
                          <s.icon className="w-4 h-4" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-slate-900">{s.label}</span>
                          <span className="block text-xs text-slate-500">{s.desc}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
              className="px-3 py-2 text-sm font-medium text-slate-200 hover:text-white transition-colors rounded-lg"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={SIGNIN_URL || "#"}
            onClick={handleSignIn}
            data-testid="nav-signin-btn"
            className="text-sm font-medium text-slate-200 hover:text-white transition-colors px-3 py-2"
          >
            Sign In
          </a>
          <Link
            to="/contact"
            data-testid="nav-book-demo-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-5 py-2.5 rounded-full hover:shadow-lg hover:shadow-emerald-500/30 hover:scale-[1.03] active:scale-[0.98] transition-all ayo-pulse"
          >
            Book a Demo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white bg-white/10 border border-white/10"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>
    </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed top-16 left-0 right-0 bottom-0 z-40 bg-slate-900/95 backdrop-blur-xl overflow-y-auto"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
          >
            <div className="px-6 py-6 space-y-1">
              <p className="text-xs uppercase tracking-widest text-teal-400 font-semibold px-3 mb-2">Solutions</p>
              {solutions.map((s) => (
                <Link
                  key={s.label}
                  to={s.to}
                  data-testid={`mobile-solution-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-white hover:bg-white/5"
                >
                  <s.icon className="w-5 h-5 text-teal-400" /> {s.label}
                </Link>
              ))}
              <div className="h-px bg-white/10 my-3" />
              {navLinks.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
                  className="block px-3 py-3 rounded-xl text-white text-base font-medium hover:bg-white/5"
                >
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-white/10 my-3" />
              <a href={SIGNIN_URL || "#"} onClick={handleSignIn} data-testid="mobile-signin-btn" className="block px-3 py-3 rounded-xl text-white font-medium hover:bg-white/5">Sign In</a>
              <Link
                to="/contact"
                data-testid="mobile-book-demo-btn"
                className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-5 py-3.5 rounded-full"
              >
                Book a Demo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
